import { throttle, debounce, camelCase } from 'lodash';
import StickySidebar from '../../libs/StickySidebar'; // includes viewport spacing fix

import MatchHeight from '../../libs/MatchHeight';
import setupShareLinks from '../../libs/setupShareLinks';
import { whatBrowser } from '../../libs/util';

import Tags from './Tags';
import PageMenu from './PageMenu';
import NinjaForms from './NinjaForms';

// ---- tiny feature detection: object-fit ----
function supportsObjectFit() {
    if ('objectFit' in document.documentElement.style) return true;
    if (window.CSS && typeof CSS.supports === 'function') {
        try { return CSS.supports('object-fit: cover'); } catch (e) { /* no-op */ }
    }
    return false;
}

class UiHelper {
    static init() {
        // Optional: legacy Safari hook (only keep if you actually target it in CSS/JS)
        if (whatBrowser.name === 'Safari' && parseInt(whatBrowser.version, 10) < 11) {
            document.body.classList.add('safari-lt-11');
        }

        // ---- handlers registry ----
        this.handlers = {
            resize: { throttle: [], debounce: [] },
            gmapsLoaded: [],
            fontsLoaded: []
        };

        // ---- DOM node caches ----
        this.domNodes = { single: {}, multiple: {} };

        // ---- resize handling ----
        window.addEventListener('resize', throttle(e => this.handleResize(e, 'throttle'), 100), { passive: true });
        window.addEventListener('resize', debounce(e => this.handleResize(e, 'debounce'), 100), { passive: true });
        this.initHandleResize();

        // primary nav width recalculation on resize
        this.setPrimaryNavWidth = this.setPrimaryNavWidth.bind(this);
        this.registerHandler('resize', this.setPrimaryNavWidth, 'throttle');

        // ---- sticky sidebar ----
        this.stickySidebar();

        // ---- share links ----
        setupShareLinks();

        // ---- NinjaForms ----
        const nf = new NinjaForms();
        nf.init();

        // ---- font-loaded callbacks (trigger this via whenFontsLoaded if you use webfonts) ----
        this.registerHandler('fontsLoaded', () => { new MatchHeight(); });
        this.registerHandler('fontsLoaded', () => { Tags.init(); });
        this.registerHandler('fontsLoaded', () => { PageMenu.init(); });

        // ---- Google Maps ----
        this.googleMapsReady = false;

        // ---- object-fit fallback ----
        this.fixUnsupportedObjectFit();
    }

    static registerHandler(e, fn, type = false) {
        if (type) this.handlers[e][type].push(fn);
        else this.handlers[e].push(fn);
    }

    static initHandleResize() {
        this.setPrimaryNavWidth();
    }

    static handleResize(e = null, type) {
        this.handlers.resize[type].forEach(fn => fn(e));
    }

    static handleFontsLoaded() {
        this.handlers.fontsLoaded.forEach(fn => fn());
    }

    static setPrimaryNavWidth() {
        const nav = this.getNode('.js-primary-nav');
        this.primaryNavWidth = nav ? nav.offsetWidth : 0;
    }

    static getPrimaryNavWidth() {
        return this.primaryNavWidth;
    }

    static getNode(s, reset = false) {
        const ccs = this.camelCaseNodeSelector(s);
        if (!this.domNodes.single[ccs] || reset) this.domNodes.single[ccs] = document.querySelector(s);
        return this.domNodes.single[ccs];
    }

    static camelCaseNodeSelector(s) {
        return s[0] + camelCase(s);
    }

    static getNodes(s, reset = false) {
        const ccs = this.camelCaseNodeSelector(s);
        if (!this.domNodes.multiple[ccs] || reset) this.domNodes.multiple[ccs] = document.querySelectorAll(s);
        return this.domNodes.multiple[ccs];
    }

    static stickySidebar() {
        this.stickyEl = document.querySelector('.js-sticky');
        if (this.stickyEl) {
            this.stickySidebar = new StickySidebar(this.stickyEl, {
                topSpacing: window.innerWidth > 1199 ? 135 : 115,
                bottomSpacing: 30,
                containerSelector: '.js-sticky-container',
                innerWrapperSelector: '.js-sticky__inner',
                stickyClass: 'sticky--stuck',
                minWidth: 992
            });
            // Small delayed update for layout shifts
            setTimeout(() => { this.stickySidebar.updateSticky(); }, 1000);
        }
    }

    static fixUnsupportedObjectFit() {
        if (supportsObjectFit()) return;

        Array.from(this.getNodes('.hero, .image-container--cover')).forEach(imageContainer => {
            const img = imageContainer.querySelector('img');
            if (!img) return;

            const src =
                (img.dataset && (img.dataset.src || img.dataset.srcset)) ||
                img.currentSrc ||
                img.getAttribute('src') ||
                '';

            if (!src) return;

            imageContainer.classList.add('bg-img-fallback');
            // Ensure any direct <img> doesn't show stretched while we use background-image
            img.style.opacity = '0';
            img.style.visibility = 'hidden';

            imageContainer.style.backgroundImage = `url('${src}')`;
            imageContainer.style.backgroundPosition = 'center';
            imageContainer.style.backgroundRepeat = 'no-repeat';
            imageContainer.style.backgroundSize = 'cover';
        });
    }

    static googleMapsApiHasLoaded() {
        this.handlers.gmapsLoaded.forEach(fn => fn());
    }

    static scrollTo(elementOrSelector) {
        const el = (typeof elementOrSelector === 'string') ? UiHelper.getNode(elementOrSelector) : elementOrSelector;
        if (!el) return;
        const scrollTop = window.scrollY || window.pageYOffset || document.body.scrollTop + ((document.documentElement && document.documentElement.scrollTop) || 0);
        const elY = el.getBoundingClientRect().top + scrollTop;
        window.scroll({ top: elY - 100, left: 0, behavior: 'smooth' });
    }
}

export default UiHelper;

// Expose Google Maps callback globally if needed
window.googleMapsApiHasLoaded = UiHelper.googleMapsApiHasLoaded.bind(UiHelper);
